import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, Tooltip, Menu, MenuItem, Typography } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';

function MediafileActionsBar({ mediafile, actions, layout = 'grid' }) {
	const [anchorEl, setAnchorEl] = useState(null);

	const visibleActions = actions.filter((action) => action.shouldDisplay(mediafile));

	const leftAction = visibleActions.find((a) => a.keyName === 'zoomIn') || null;
	const centerAction = visibleActions.find((a) => a.keyName === 'chip') || null;
	const overflowActions = visibleActions.filter(
		(a) => a.keyName !== 'zoomIn' && a.keyName !== 'chip'
	);

	const handleOpenPopover = (event) => setAnchorEl(event.currentTarget);
	const handleClosePopover = () => setAnchorEl(null);

	const renderActionButton = (action, key) => {
		if (action.CustomComp) {
			return <action.CustomComp key={key} mediafile={mediafile} />;
		}
		return (
			<Tooltip
				key={key}
				title={action.CustomTooltip ? action.CustomTooltip(mediafile) : action.tooltip || ''}
				arrow
			>
				<IconButton
					size="small"
					onClick={(e) =>
						typeof action.clickHandler === 'function' && action.clickHandler(mediafile, e)
					}
					sx={{
						padding: '4px',
						border: '1px solid',
						borderRadius: '50%',
						color: 'inherit',
						'&:hover': { backgroundColor: 'rgba(0,0,0,0.1)' },
					}}
				>
					{action.icon}
				</IconButton>
			</Tooltip>
		);
	};

	if (layout === 'detail') {
		// All actions in one row
		return (
			<Box display="flex" gap={1} flexWrap="wrap">
				{visibleActions.map((action) => renderActionButton(action, action.keyName))}
			</Box>
		);
	}

	return (
		<Box display="grid" gridTemplateColumns="40px 1fr 40px" alignItems="center" width="100%">
			{/* LEFT (Zoom) */}
			<Box display="flex" justifyContent="center">
				{leftAction && renderActionButton(leftAction, leftAction.keyName)}
			</Box>

			{/* CENTER (Chip) */}
			<Box
				display="flex"
				justifyContent="center"
				minWidth={0}
				sx={{
					// mr: '-10px',
					mr: overflowActions.length ? '-10px' : 0, // optical shift to the right because of MoreVertIcon
				}}
			>
				{centerAction ? (
					renderActionButton(centerAction, centerAction.keyName)
				) : (
					<Box sx={{ height: 32 }} />
				)}
			</Box>

			{/* RIGHT */}
			<Box display="flex" justifyContent="center">
				{overflowActions.length > 0 && (
					<>
						<IconButton size="small" onClick={handleOpenPopover}>
							<MoreVertIcon />
						</IconButton>

						<Menu
							anchorEl={anchorEl}
							open={Boolean(anchorEl)}
							onClose={handleClosePopover}
							anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
							transformOrigin={{ vertical: 'top', horizontal: 'right' }}
						>
							{overflowActions.map((action) => {
								const tooltipText = action.CustomTooltip?.(mediafile) || action.tooltip || '';

								const isCustomTextComponent = [
									'addToPlaylist',
									'shareToSocialMedia',
									'bulkAddToScreens',
								].includes(action.keyName);

								return (
									<MenuItem
										key={action.keyName}
										onClick={(e) => {
											if (typeof action.clickHandler === 'function') {
												action.clickHandler(mediafile, e);
											}
										}}
										sx={{
											display: 'flex',
											alignItems: 'center',
											gap: 1,
											width: '100%',
										}}
									>
										{/* ICON / CustomComp */}
										{action.CustomComp ? (
											isCustomTextComponent ? (
												<action.CustomComp
													mediafile={mediafile}
													variant="menuItem"
													menuText={tooltipText}
												/>
											) : (
												<action.CustomComp mediafile={mediafile} />
											)
										) : (
											<Box
												sx={{
													width: 32,
													height: 32,
													borderRadius: '50%',
													border: '1px solid',
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													flexShrink: 0,
												}}
											>
												{action.icon}
											</Box>
										)}

										{/* TEXT */}
										{!isCustomTextComponent && tooltipText && (
											<Typography
												variant="body2"
												sx={{
													overflow: 'hidden',
													textOverflow: 'ellipsis',
													whiteSpace: 'nowrap',
												}}
											>
												{tooltipText}
											</Typography>
										)}
									</MenuItem>
								);
							})}
						</Menu>
					</>
				)}
			</Box>
		</Box>
	);
}

MediafileActionsBar.propTypes = {
	mediafile: PropTypes.object.isRequired,
	actions: PropTypes.arrayOf(
		PropTypes.shape({
			keyName: PropTypes.string.isRequired,
			shouldDisplay: PropTypes.func.isRequired,
			CustomComp: PropTypes.elementType,
			icon: PropTypes.node,
			tooltip: PropTypes.string,
			CustomTooltip: PropTypes.func,
			clickHandler: PropTypes.func,
		})
	).isRequired,
	layout: PropTypes.oneOf(['grid', 'table', 'detail']),
};

export default MediafileActionsBar;
